import { getUnitCount } from '../../helpers/getUnitCount';
import CalibrationKindSelector from '../CalibrationKindSelector';
import PartConditionField from '../PartConditionField';
import PartSelector from '../PartSelector';
import PartSerialNumberField from '../PartSerialNumberField';
import TargetStatusLine from '../TargetStatusLine';
import UnitSelector from '../UnitSelector';

import { VerticalContainer } from '@american-autonomy/ag-ui';

import { getDroneIntervals } from 'pages/MaintenancePage/helpers/getDroneIntervals';
import { getModelParts } from 'pages/MaintenancePage/helpers/getModelParts';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const ServiceTargetFields: FunctionComponent<Props> = ({
    className = '',
    draft,
    drone,
    isRemainingLifeValid,
    onChangeCalibrationKind,
    onChangePart,
    onChangePartCondition,
    onChangePartSerialNumber,
    onChangeRemainingLife,
    onChangeUnit,
    serviceIntervals,
    testId = TEST_ID
}) => {
    const isPartTargeted = draft.eventType === 'replaced' || draft.eventType === 'serviced';
    // An inspection may optionally target a part — with no part chosen it stays a
    // general inspection, so the picker is offered but never required.
    const isPartSelectable = isPartTargeted || draft.eventType === 'inspected';
    const unitCount = draft.partType ? getUnitCount({ drone, partType: draft.partType }) : 0;
    const droneIntervals = getDroneIntervals({ drone, serviceIntervals });
    const interval = draft.partType ? droneIntervals[draft.partType] : undefined;
    // SR-23 — the condition field appears once the replacement target is fully
    // identified (part, plus a unit choice on multi-unit parts).
    const isTargetChosen = unitCount <= 1 || draft.unitSelection.kind !== 'unchosen';
    // One serial cannot describe every unit, so the field hides on all-units picks;
    // the draft hook clears any entered value when all-units is chosen.
    const isSerialShown =
        isPartSelectable &&
        Boolean(draft.partType) &&
        isTargetChosen &&
        draft.unitSelection.kind !== 'allUnits';

    return (
        <VerticalContainer className={`${styles.serviceTargetFields} ${className}`} testId={testId}>
            {isPartSelectable && (
                <PartSelector
                    onSelect={onChangePart}
                    options={getModelParts(drone.model)}
                    selectedPart={draft.partType}
                />
            )}

            {draft.eventType === 'calibrated' && (
                <CalibrationKindSelector
                    onSelect={onChangeCalibrationKind}
                    selectedKind={draft.calibrationKind}
                />
            )}

            {isPartSelectable && draft.partType && unitCount > 1 && (
                <UnitSelector
                    onSelect={onChangeUnit}
                    partType={draft.partType}
                    unitCount={unitCount}
                    unitSelection={draft.unitSelection}
                />
            )}

            {draft.eventType === 'replaced' && draft.partType && isTargetChosen && interval && (
                <PartConditionField
                    interval={interval}
                    isRemainingLifeValid={isRemainingLifeValid}
                    onChangePartCondition={onChangePartCondition}
                    onChangeRemainingLife={onChangeRemainingLife}
                    partCondition={draft.partCondition}
                    remainingLife={draft.remainingLife}
                />
            )}

            {isSerialShown && (
                <PartSerialNumberField
                    onChange={onChangePartSerialNumber}
                    partSerialNumber={draft.partSerialNumber}
                />
            )}

            <TargetStatusLine draft={draft} drone={drone} serviceIntervals={droneIntervals} />
        </VerticalContainer>
    );
};

export default ServiceTargetFields;
